import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { ScreenComponent, ScreenProps } from '../types/screen';

export interface ModalApi<TResult = unknown> {
  resolve: (result: TResult) => void;
  reject: (reason?: unknown) => void;
}

export type ModalRenderer<TResult = unknown> = (api: ModalApi<TResult>) => ReactNode;

interface QueuedModal {
  key: number;
  render: ModalRenderer;
  resolve: (result: unknown) => void;
  reject: (reason?: unknown) => void;
}

export interface ModalHostContextValue {
  openModal: <TResult = unknown>(render: ModalRenderer<TResult>) => Promise<TResult>;
  openScreen: <TInput = unknown, TResult = unknown>(
    Component: ScreenComponent<TInput, TResult>,
    input?: TInput,
  ) => Promise<TResult>;
  closeAll: () => void;
  active: boolean;
  pending: number;
}

const ModalHostContext = createContext<ModalHostContextValue | null>(null);

export const ModalHostProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<QueuedModal[]>([]);
  const keyRef = useRef(0);

  const openModal = useCallback(<TResult = unknown,>(render: ModalRenderer<TResult>): Promise<TResult> => {
    return new Promise<TResult>((resolve, reject) => {
      const key = ++keyRef.current;
      setQueue((q) => [
        ...q,
        {
          key,
          render: render as ModalRenderer,
          resolve: resolve as (r: unknown) => void,
          reject,
        },
      ]);
    });
  }, []);

  const openScreen = useCallback(
    <TInput = unknown, TResult = unknown>(
      Component: ScreenComponent<TInput, TResult>,
      input?: TInput,
    ): Promise<TResult> => {
      return openModal<TResult>((api) => {
        const props: ScreenProps<TInput, TResult> = {
          mode: 'modal',
          input,
          onComplete: (r) => { api.resolve(r); },
          onCancel: () => { api.reject({ cancelled: true }); },
        };
        return Component(props);
      });
    },
    [openModal],
  );

  const finish = useCallback((key: number) => {
    setQueue((q) => q.filter((m) => m.key !== key));
  }, []);

  const closeAll = useCallback(() => {
    setQueue((q) => {
      for (const m of q) m.reject({ cancelled: true, reason: 'closeAll' });
      return [];
    });
  }, []);

  const value = useMemo<ModalHostContextValue>(
    () => ({
      openModal,
      openScreen,
      closeAll,
      active: queue.length > 0,
      pending: queue.length,
    }),
    [openModal, openScreen, closeAll, queue.length],
  );

  return (
    <ModalHostContext.Provider value={value}>
      {children}
      <ModalHost
        queue={queue}
        onResolved={(key, result) => {
          const m = queue.find((x) => x.key === key);
          if (m) m.resolve(result);
          finish(key);
        }}
        onRejected={(key, reason) => {
          const m = queue.find((x) => x.key === key);
          if (m) m.reject(reason);
          finish(key);
        }}
      />
    </ModalHostContext.Provider>
  );
};

interface ModalHostProps {
  queue: QueuedModal[];
  onResolved: (key: number, result: unknown) => void;
  onRejected: (key: number, reason?: unknown) => void;
}

const ModalHost: React.FC<ModalHostProps> = ({ queue, onResolved, onRejected }) => {
  if (queue.length === 0) return null;
  // Render only the front of the queue. Strict FIFO: no concurrent modals.
  const head = queue[0];
  const api: ModalApi = {
    resolve: (r) => { onResolved(head.key, r); },
    reject: (reason) => { onRejected(head.key, reason); },
  };
  return <React.Fragment key={head.key}>{head.render(api)}</React.Fragment>;
};

export function useModalHost(): ModalHostContextValue {
  const ctx = useContext(ModalHostContext);
  if (!ctx) throw new Error('useModalHost must be used within ModalHostProvider');
  return ctx;
}
